import {
  promotionApi as eatsPromotionApi,
  type ListMerchantPromotionsParams,
  type PromotionSelection,
} from '@noor/eats-api';
import {
  toPromotion,
  toPromotionCampaign,
  toPromotionDetail,
} from '../lib/map-promotion';
import type { Promotion, PromotionDetail, Promotions } from '../model/schema';

/**
 * The write payload for a promotion.
 *
 * Both id fields are required by the API (a promotion is always product- and
 * branch-scoped); the display names ride along so a table row can be rebuilt
 * from the response without a second lookup.
 */
export interface PromotionInput {
  productId: string;
  productName: string;
  restaurantId: string;
  branchName: string;
  /**
   * The campaign's own name, in all three platform languages.
   *
   * The merchant TYPES this now (wizard step 1). It used to be derived from
   * the product name, which meant a batch across five products was named after
   * whichever one happened to be first, and the name changed if the product
   * was renamed. Ru is the canonical member every read falls back to; a blank
   * uz/en is "not translated yet", which the backend leaves alone.
   */
  nameTranslations: { ru: string; uz?: string; en?: string };
  imageUrl?: string;
  price: number;
  discountPercent: number;
  discountType: 'percent' | 'fixed';
  finalPrice: number;
  /** Preformatted range for display; `null` = open-ended. */
  duration?: string | null;
  /**
   * ISO bounds actually sent to the backend. `undefined` leaves the stored
   * bound alone; `null` CLEARS it — which is what an open-ended promo needs,
   * since omitting the field left the previous window in place.
   */
  startsAt?: string | null;
  endsAt?: string | null;
}

/**
 * Build the API's promotion terms from a form input.
 *
 * `name` is a `v1LocalizedText` — sending a plain string fails the whole
 * request with `400 failed to encode payload` — and it now carries what the
 * merchant TYPED in wizard step 1 rather than the product's name. Blank uz/en
 * members go as `''`, which the backend reads as "not provided" and leaves
 * whatever is stored alone.
 *
 * NO `image` member: the write bodies dropped it, and the gateway rejects
 * unknown fields. The `imageUrl` on the input is display-only anyway — a
 * one-way expanded URL a write must never echo back.
 */
function toTerms(input: PromotionInput) {
  return {
    name: {
      ru: input.nameTranslations.ru,
      uz: input.nameTranslations.uz ?? '',
      en: input.nameTranslations.en ?? '',
    },
    promo_type: 'PROMO_TYPE_DISCOUNT' as const,
    discount_type:
      input.discountType === 'fixed'
        ? ('DISCOUNT_TYPE_FIXED' as const)
        : ('DISCOUNT_TYPE_PERCENTAGE' as const),
    // A fixed-price promo stores the final price; a percentage stores the rate.
    discount_value:
      input.discountType === 'fixed' ? input.finalPrice : input.discountPercent,
    // `null` is forwarded deliberately (it clears the bound); only an absent
    // value is dropped so the stored window survives an unrelated edit.
    ...(input.startsAt !== undefined ? { starts_at: input.startsAt } : {}),
    ...(input.endsAt !== undefined ? { ends_at: input.endsAt } : {}),
  };
}

/**
 * What a promotion write actually did — the same applied/pending split as the
 * menu writes: a gated merchant's create/update/delete is QUEUED behind
 * moderation, answering with a receipt instead of the promotion. That is a
 * success (never an error toast), but the table will not show the change until
 * a moderator approves it, so callers surface the queued case explicitly.
 */
export interface PromotionWriteOutcome {
  status: 'applied' | 'pending';
  /** Rebuilt table rows; empty on a pending CREATE (no server id exists yet). */
  rows: Promotion[];
}

/** Rebuild a table row from the written promotion's id plus its form input. */
function toRow(id: Promotion['id'], input: PromotionInput): Promotion {
  return {
    id,
    productId: input.productId,
    restaurantId: input.restaurantId,
    productName: input.productName,
    // The row's own campaign name. `ru` is the canonical member every read
    // falls back to, so an optimistic row matches what the refetch will show.
    name: input.nameTranslations.ru,
    ...(input.imageUrl ? { imageUrl: input.imageUrl } : {}),
    price: input.price,
    discountPercent: input.discountPercent,
    discountType: input.discountType,
    finalPrice: input.finalPrice,
    branchName: input.branchName,
    duration: input.duration ?? null,
  };
}

/**
 * What the promotions filter bar can narrow the list by.
 *
 * The date bounds are the URL's own `YYYY-MM-DD` strings; `toListParams`
 * widens them to the RFC3339 instants the endpoint wants.
 *
 * NO `tag`: the filter bar offers a tag select, but neither the promotions
 * endpoint nor any other has a tag taxonomy (CLAUDE.md §8, "What has NO API
 * source"), so there is nothing to send and nothing to match on. It is
 * deliberately absent here rather than accepted and dropped, so a caller
 * cannot believe it was applied.
 */
export interface PromotionFilters {
  /** Narrows to one product category. */
  categoryId?: string | undefined;
  /** Inclusive `YYYY-MM-DD` start of the window a promotion must overlap. */
  from?: string | undefined;
  /** Inclusive `YYYY-MM-DD` end of that window. */
  to?: string | undefined;
  /** When true, fetch published promotions visible to clients (Режим витрина). */
  showcase?: boolean | undefined;
}

export interface PromotionListScope {
  branches: { id: string; name: string }[];
}

/**
 * The UTC offset the range bounds are stamped with.
 *
 * `range_start`/`range_end` are the ONE place the merchant API demands a
 * zone-qualified instant. Its parse layout is
 * `2006-01-02T15:04:05.999999999Z07:00`, whose trailing `Z07:00` is NOT
 * optional — a naive `2026-08-01T00:00:00` fails with
 * `cannot parse "" as "Z07:00"` and the whole request 400s.
 *
 * That makes these two the exception to CLAUDE.md §8 #2 ("timestamps are
 * naive"), which holds for every timestamp the API RETURNS and for the bodies
 * that echo them back — hence the offset lives here, on the two params that
 * need it, rather than in a shared formatter that would break the rest.
 *
 * `+05:00` is Uzbekistan (UTC+5, no DST), which is the timezone the merchant's
 * own calendar dates are picked in — so a window of "1–31 August" means the
 * merchant's August, not the browser's. Using the BROWSER's offset instead
 * would silently shift the window for anyone travelling or on a mis-set clock.
 */
const RANGE_UTC_OFFSET = '+05:00';

/**
 * Map the UI's filters onto the endpoint's query params.
 *
 * The bounds become an OVERLAP window, which is why `to` is widened to the end
 * of its day: `range_end` is an instant, so passing the bare date would exclude
 * every promotion running later that same day — a merchant filtering "today"
 * would see nothing.
 *
 * Both carry {@link RANGE_UTC_OFFSET}; see there for why these two timestamps
 * are zone-qualified where the rest of the API's are naive.
 *
 * Empty values are omitted rather than sent blank; the gateway treats an empty
 * `category_id` as a real (unmatched) value.
 */
function toListParams(filters: PromotionFilters): ListMerchantPromotionsParams {
  return {
    ...(filters.categoryId ? { category_id: filters.categoryId } : {}),
    ...(filters.from
      ? { range_start: `${filters.from}T00:00:00${RANGE_UTC_OFFSET}` }
      : {}),
    ...(filters.to ? { range_end: `${filters.to}T23:59:59${RANGE_UTC_OFFSET}` } : {}),
  };
}

export const promotionApi = {
  /**
   * The merchant's promotions as table rows.
   *
   * One branch in scope reads that branch's own list (`by-restaurant`). Every
   * other case («Все филиалы») reads `GET /promotions/by-merchant` ONCE — the
   * backend joins each promotion to its product and branch (carrying
   * `moderation_state`), so one request fills the table instead of a burst of
   * one per branch. That endpoint also applies `category_id` and the overlap
   * window (`range_start`/`range_end`) server-side, so the filter bar narrows
   * the query rather than the rendered array. Only keys the endpoint actually
   * accepts are forwarded; see {@link PromotionFilters} for the one the UI
   * offers that it does not.
   *
   * NO API SOURCE for the filter taxonomies or the header's date-range label:
   * the endpoint takes a `category_id` but exposes no list of categories or
   * tags. The page feeds those selects from the MENU query instead, so they
   * are returned empty here rather than invented.
   */
  async list(
    filters: PromotionFilters = {},
    scope: PromotionListScope = { branches: [] },
    options?: { signal?: AbortSignal },
  ): Promise<Promotions> {
    if (filters.showcase) {
      const restaurantId =
        scope.branches.length === 1 ? scope.branches[0]?.id : undefined;
      const { list } = await eatsPromotionApi.listPublished(
        restaurantId ? { restaurant_id: restaurantId } : undefined,
        options,
      );
      return {
        items: list
          .map(toPromotion)
          .filter((promotion) => matchesDateFilter(promotion, filters)),
        categories: [],
        tags: [],
        dateRangeLabel: '',
      };
    }

    // One branch selected: that branch's own list — a single `by-restaurant`
    // read scoped to the branch, as before.
    if (scope.branches.length === 1) {
      const branch = scope.branches[0]!;
      const { list } = await eatsPromotionApi.listByRestaurant(
        branch.id,
        { active_only: false },
        options,
      );
      return {
        items: groupCampaigns(
          list.map((promotion) => toPromotionCampaign(promotion, branch.name)),
        ).filter((promotion) => matchesDateFilter(promotion, filters)),
        categories: [],
        tags: [],
        dateRangeLabel: '',
      };
    }

    // «Все филиалы»: ONE `GET /promotions/by-merchant` read instead of a
    // `by-restaurant` burst per branch. The endpoint answers with the joined
    // `product × restaurant × promotion` rows (each carrying `moderation_state`
    // and the branch name), and applies `category_id` + the overlap window
    // server-side — the per-branch route accepts neither. Rows are grouped
    // back into one campaign row each, so the table keeps its granularity.
    const { list } = await eatsPromotionApi.listByMerchant(
      toListParams(filters),
      options,
    );
    return {
      items: groupCampaigns(list.map(toPromotion)).filter((promotion) =>
        matchesDateFilter(promotion, filters),
      ),
      categories: [],
      tags: [],
      dateRangeLabel: '',
    };
  },

  async productRows(
    filters: PromotionFilters = {},
    options?: { signal?: AbortSignal },
  ): Promise<Promotions> {
    const { list } = await eatsPromotionApi.listByMerchant(
      toListParams(filters),
      options,
    );
    return {
      items: list.map(toPromotion),
      categories: [],
      tags: [],
      dateRangeLabel: '',
    };
  },

  /**
   * One promotion's own terms AT ONE BRANCH, for seeding the edit form — `GET
   * /promotions/{id}/by-merchant?restaurant_id=…`. Answers with the ids the
   * table row can only name, so the form no longer has to match a product and
   * branch by string.
   *
   * `restaurantId` is required by the endpoint (a call without it 400s) and it
   * SCOPES the answer: `productIds` is what the promotion covers at that branch
   * alone. A campaign spanning several branches is therefore read once per
   * branch — which is exactly the shape the wizard's «Филиал продукта» step
   * needs, since its checkboxes are per branch too.
   */
  async get(
    id: Promotion['id'],
    restaurantId: string,
    options?: { signal?: AbortSignal },
  ): Promise<PromotionDetail> {
    return toPromotionDetail(
      await eatsPromotionApi.get(String(id), restaurantId, options),
    );
  },

  /** Create one promotion (one product on one branch). */
  async create(input: PromotionInput): Promise<PromotionWriteOutcome> {
    const { promotion } = await eatsPromotionApi.create({
      restaurant_id: input.restaurantId,
      product_ids: [input.productId],
      ...toTerms(input),
    });
    // A gated create is queued — there is no server id to build a row from.
    if (!promotion) return { status: 'pending', rows: [] };
    return { status: 'applied', rows: [toRow(promotion.id, input)] };
  },

  /**
   * Create one promotion covering several branches in ONE request.
   *
   * The inputs are grouped into `selections` — one entry per branch, naming
   * only the products the promotion covers THERE. That replaced the old
   * `restaurant_ids × product_ids` cross product, which could not express
   * "this product only at that branch": a merchant discounting one item at one
   * location had to create the promotion once per branch, and a batch across
   * two branches always applied every product to both.
   *
   * The two shapes must never be MIXED — the gateway answers 400 — so this
   * sends `selections` alone.
   */
  async createBatch(inputs: PromotionInput[]): Promise<PromotionWriteOutcome> {
    const first = inputs[0];
    if (!first) return { status: 'applied', rows: [] };

    const { promotion } = await eatsPromotionApi.createBatch({
      selections: toSelections(inputs),
      ...toTerms(first),
    });
    if (!promotion) return { status: 'pending', rows: [] };
    // The batch endpoint answers with a single promotion covering every
    // selection, so each row echoes that id.
    return {
      status: 'applied',
      rows: inputs.map((input) => toRow(promotion.id, input)),
    };
  },

  async update(
    id: Promotion['id'],
    input: PromotionInput,
  ): Promise<PromotionWriteOutcome> {
    const { promotion } = await eatsPromotionApi.update(String(id), toTerms(input));
    // A gated update is queued; the row keeps its existing id either way.
    return {
      status: promotion ? 'applied' : 'pending',
      rows: [toRow(promotion?.id ?? id, input)],
    };
  },

  /**
   * Retire a whole promotion — `DELETE /promotions/{id}`.
   *
   * Campaign-wide: the discount goes from every product and branch the
   * promotion covers. NOT what the table's per-row delete calls — a row is one
   * product × branch pair, so retiring the campaign from it would take every
   * sibling row down too; that delete narrows the campaign via
   * {@link detachProducts}, as does an EDIT that unchecks a product.
   *
   * `pending` means the delete was QUEUED behind moderation — the rows survive
   * until a moderator approves it, so the caller says so instead of letting the
   * refetched rows read as a failed delete.
   */
  remove(id: Promotion['id']): Promise<{ pending: boolean }> {
    return eatsPromotionApi.remove(String(id));
  },

  /**
   * Unlink products from a promotion — `DELETE /promotions/{id}/products`.
   *
   * The campaign and every product NOT named here survive, which is what makes
   * this the right call both for the table's per-row delete (one product ×
   * branch pair) and for an EDIT that unchecks a product: the merchant is
   * narrowing the campaign, not retiring it. `restaurantId` narrows the unlink
   * to one branch, and is now REQUIRED — it and the ids travel in the request
   * BODY, which is what finally made this route usable through the gateway.
   */
  async detachProducts(
    id: Promotion['id'],
    productIds: string[],
    restaurantId: string,
  ): Promise<{ pending: boolean }> {
    if (productIds.length === 0) return { pending: false };
    const { pending_change: pendingChange } = await eatsPromotionApi.removeProducts(
      String(id),
      restaurantId,
      productIds,
    );
    return { pending: pendingChange !== undefined };
  },

  /**
   * Add products to an existing promotion — `POST /promotions/{id}/products`.
   */
  async attachProducts(
    id: Promotion['id'],
    productIds: string[],
    restaurantId?: string,
  ): Promise<void> {
    if (productIds.length === 0) return;
    await eatsPromotionApi.addProducts(String(id), productIds, {
      ...(restaurantId ? { restaurant_id: restaurantId } : {}),
    });
  },
};

/**
 * Fold rows into one campaign row per promotion id.
 *
 * A campaign spans every product × branch pair it covers, so both the
 * per-branch reads and the joined `by-merchant` rows repeat its id — the
 * table renders one row per campaign. The first-seen row's names win; the
 * product lists merge, which is what names the pair a delete acts on.
 */
function groupCampaigns(rows: Promotion[]): Promotion[] {
  const byPromotion = new Map<string, Promotion>();
  for (const promotion of rows) {
    const key = String(promotion.id);
    const existing = byPromotion.get(key);
    if (!existing) {
      byPromotion.set(key, promotion);
      continue;
    }
    byPromotion.set(key, {
      ...existing,
      productIds: [
        ...new Set([...(existing.productIds ?? []), ...(promotion.productIds ?? [])]),
      ],
    });
  }
  return [...byPromotion.values()];
}

function matchesDateFilter(promotion: Promotion, filters: PromotionFilters): boolean {
  const start = dayKey(promotion.startsAt);
  const end = dayKey(promotion.endsAt);
  if (filters.from && end && end < filters.from) return false;
  if (filters.to && start && start > filters.to) return false;
  return true;
}

function dayKey(timestamp: string | undefined): string | undefined {
  return timestamp?.match(/^\d{4}-\d{2}-\d{2}/)?.[0];
}

/**
 * Group the flat input rows into one `selections` entry per branch.
 *
 * The wizard hands down one input per (product, branch) pair, because that is
 * what a TABLE ROW is; the wire wants the transpose — a branch and its product
 * list. Ids are de-duplicated per branch: the same pair can legitimately arrive
 * twice when the caller fanned out over both lists.
 */
function toSelections(inputs: PromotionInput[]): PromotionSelection[] {
  const byBranch = new Map<string, Set<string>>();
  for (const input of inputs) {
    const products = byBranch.get(input.restaurantId) ?? new Set<string>();
    products.add(input.productId);
    byBranch.set(input.restaurantId, products);
  }
  return [...byBranch].map(([restaurant_id, productIds]) => ({
    restaurant_id,
    product_ids: [...productIds],
  }));
}
